import React, { useState, useCallback } from 'react';
import { Box, Container, Typography, Snackbar, Alert, Chip, Stack, Tooltip } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import HistoryIcon from '@mui/icons-material/History';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import TranslateIcon from '@mui/icons-material/Translate';
import ArticleIcon from '@mui/icons-material/Article';
import Recorder from './components/Recorder';
import TranscriptViewer from './components/TranscriptViewer';
import Translator from './components/Translator';
import RewritePanel from './components/RewritePanel';
import MeetingHistory from './components/MeetingHistory';
import ErrorBoundary from './components/ErrorBoundary';
import { getMeeting } from './api';

/* ── Tab definition ── */
const TABS = [
  { id: 0, label: 'Record',     emoji: '🎙',  alwaysEnabled: true },
  { id: 1, label: 'Transcript', emoji: '📝',  alwaysEnabled: false },
  { id: 2, label: 'Translate',  emoji: '🌐',  alwaysEnabled: false },
  { id: 3, label: 'Rewrite',    emoji: '✨',  alwaysEnabled: false },
  { id: 4, label: 'History',    emoji: '🕐',  alwaysEnabled: true },
];

/* ── Animated system status dot ── */
function StatusDot({ label, delay = '0s' }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.8}>
      <Box className="status-dot" sx={{ animationDelay: delay }} />
      <Typography variant="caption" sx={{
        fontSize: 10, color: '#374151', fontWeight: 700,
        letterSpacing: 0.4, fontFamily: '"JetBrains Mono", monospace',
      }}>
        {label}
      </Typography>
    </Stack>
  );
}

/* ── Tab panel wrapper ── */
function TabPanel({ children, value, index }) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ pt: 3 }}>
      {value === index && children}
    </Box>
  );
}

export default function App() {
  const [activeMeeting, setActiveMeeting] = useState(null);
  const [activeTab, setActiveTab]         = useState(0);
  const [snackbar, setSnackbar]           = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleMeetingReady = useCallback((meetingData) => {
    setActiveMeeting(meetingData);
    setTimeout(() => {
      setActiveTab(1);
      showSnackbar('Transcription complete! View your transcript below.', 'success');
    }, 1000);
  }, [showSnackbar]);

  const handleSelectHistory = useCallback(async (meeting) => {
    // Set basic data immediately so UI feels responsive
    setActiveMeeting(meeting);
    setActiveTab(1);
    showSnackbar('Loading meeting…', 'info');
    try {
      // Fetch full meeting data including saved summary & translations
      const fullMeeting = await getMeeting(meeting.meeting_id);
      if (fullMeeting) {
        setActiveMeeting(fullMeeting);
        const hasSummary = fullMeeting.summary && fullMeeting.summary.overall_summary;
        showSnackbar(
          hasSummary
            ? 'Meeting loaded — saved report available in Rewrite tab!'
            : 'Meeting loaded — click Rewrite to generate a report.',
          'success'
        );
      }
    } catch (err) {
      console.warn('Could not fetch full meeting data:', err.message);
    }
  }, [showSnackbar]);

  const handleTranslationDone = useCallback((lang, text) => {
    setActiveMeeting(prev => prev ? {
      ...prev,
      translated_text: { ...prev.translated_text, [lang]: text }
    } : prev);
  }, []);

  const handleRewriteDone = useCallback((summary) => {
    setActiveMeeting(prev => prev ? { ...prev, summary } : prev);
  }, []);

  const handleTabClick = (tabId) => {
    const tab = TABS.find(t => t.id === tabId);
    if (!tab) return;
    if (!tab.alwaysEnabled && !activeMeeting) return;
    setActiveTab(tabId);
  };

  return (
    <Box sx={{ minHeight: '100vh', position: 'relative', zIndex: 1 }}>
      {/* Background orbs */}
      <div className="bg-orb bg-orb-1" />
      <div className="bg-orb bg-orb-2" />
      <div className="bg-orb bg-orb-3" />
      <div className="bg-orb bg-orb-4" />

      {/* ══ FLOATING NAVBAR ══ */}
      <Box
        component="header"
        sx={{
          position: 'sticky', top: 0, zIndex: 100,
          background: 'rgba(4,4,10,0.84)',
          backdropFilter: 'blur(28px) saturate(180%)',
          borderBottom: '1px solid rgba(139,92,246,0.12)',
          boxShadow: '0 1px 0 rgba(255,255,255,0.04), 0 4px 24px rgba(0,0,0,0.4)',
        }}
      >
        <Box sx={{
          maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 4 },
          height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>

          {/* Logo */}
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{
              width: 38, height: 38, borderRadius: '11px',
              background: 'linear-gradient(135deg, #7C3AED, #06B6D4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 0 20px rgba(124,58,237,0.45), inset 0 1px 0 rgba(255,255,255,0.2)',
              flexShrink: 0,
            }}>
              <MicIcon sx={{ fontSize: 18, color: '#fff' }} />
            </Box>
            <Typography variant="h6" className="logo-shimmer" fontWeight={800} sx={{ fontSize: 17 }}>
              TANIFY
            </Typography>
            <Chip label="AI" size="small" sx={{
              background: 'rgba(139,92,246,0.15)',
              color: '#A78BFA',
              border: '1px solid rgba(139,92,246,0.3)',
              height: 20, fontSize: 10, fontWeight: 900, letterSpacing: 0.5,
            }} />
          </Stack>

          {/* Center: system status */}
          <Stack direction="row" spacing={3} sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
            <StatusDot label="WHISPER READY" delay="0s" />
            <StatusDot label="TRANSLATE API" delay="0.8s" />
            <StatusDot label="DATABASE" delay="1.6s" />
          </Stack>

          {/* Right */}
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography variant="caption" sx={{
              color: '#374151', fontSize: 11,
              display: { xs: 'none', sm: 'block' },
              fontFamily: '"JetBrains Mono", monospace',
            }}>
              Meeting AI Assistant
            </Typography>
            <Box sx={{
              px: 1.4, py: 0.5, borderRadius: '8px',
              background: 'rgba(139,92,246,0.1)',
              border: '1px solid rgba(139,92,246,0.2)',
            }}>
              <Typography variant="caption" sx={{
                color: '#8B5CF6', fontWeight: 800, fontSize: 10,
                fontFamily: '"JetBrains Mono", monospace',
              }}>
                v1.1.9
              </Typography>
            </Box>
          </Stack>

        </Box>
      </Box>

      {/* ══ MAIN CONTENT ══ */}
      <Container maxWidth="lg" sx={{ py: 4, position: 'relative', zIndex: 1 }}>

        {/* ── Hero ── */}
        <Box textAlign="center" mb={4} className="scan-line-wrap">
          <Typography
            variant="h3"
            fontWeight={800}
            mb={1.5}
            sx={{
              fontFamily: '"Space Grotesk", sans-serif',
              fontSize: { xs: '1.75rem', sm: '2.2rem', md: '2.9rem' },
              lineHeight: 1.15,
            }}
          >
            <span className="gradient-text">Record. Transcribe. Summarize.</span>
          </Typography>

          <Typography variant="body1" sx={{ color: '#475569', maxWidth: 480, mx: 'auto', mb: 3, fontSize: 15, lineHeight: 1.6 }}>
            AI-powered meeting assistant — capture every word, translate &amp; rewrite instantly.
          </Typography>

          {/* Live status pills */}
          <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap" useFlexGap>
            {[
              { dot: '#EF4444', label: '● LIVE', pulse: true },
              { dot: '#8B5CF6', label: '≋ AI READY', pulse: false },
              { dot: '#10B981', label: '⟳ 99.9% UPTIME', pulse: false },
            ].map(({ dot, label, pulse }) => (
              <Box key={label} sx={{
                display: 'flex', alignItems: 'center', gap: 0.8,
                px: 1.5, py: 0.6, borderRadius: '20px',
                background: 'rgba(8,8,18,0.7)',
                border: '1px solid rgba(139,92,246,0.12)',
                backdropFilter: 'blur(10px)',
              }}>
                <Typography variant="caption" sx={{
                  color: '#475569', fontSize: 11, fontWeight: 700,
                  fontFamily: '"JetBrains Mono", monospace', letterSpacing: 0.4,
                }}>
                  {label}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        {/* ── Custom Pill Tab Bar ── */}
        <Box sx={{ mb: 3 }}>
          <div className="tab-strip">
            {TABS.map((tab) => {
              const isDisabled = !tab.alwaysEnabled && !activeMeeting;
              const isActive   = activeTab === tab.id;
              return (
                <Tooltip
                  key={tab.id}
                  title={isDisabled ? 'Complete a recording first' : ''}
                  arrow placement="bottom"
                >
                  <button
                    className={`tab-pill ${isActive ? 'tab-pill-active' : ''} ${isDisabled ? 'tab-pill-disabled' : ''}`}
                    onClick={() => handleTabClick(tab.id)}
                    aria-label={tab.label}
                    aria-selected={isActive}
                  >
                    <span style={{ fontSize: 14 }}>{tab.emoji}</span>
                    <span>{tab.label}</span>
                  </button>
                </Tooltip>
              );
            })}
          </div>
        </Box>

        {/* ── Tab Panels ── */}
        <TabPanel value={activeTab} index={0}>
          <Recorder onMeetingReady={handleMeetingReady} showSnackbar={showSnackbar} />
        </TabPanel>
        <TabPanel value={activeTab} index={1}>
          {activeMeeting && (
            <ErrorBoundary key={`t-${activeMeeting.meeting_id}`} onReset={() => setActiveTab(4)}>
              <TranscriptViewer meeting={activeMeeting} />
            </ErrorBoundary>
          )}
        </TabPanel>
        <TabPanel value={activeTab} index={2}>
          {activeMeeting && (
            <ErrorBoundary key={`tr-${activeMeeting.meeting_id}`} onReset={() => setActiveTab(4)}>
              <Translator meeting={activeMeeting} onTranslationDone={handleTranslationDone} showSnackbar={showSnackbar} />
            </ErrorBoundary>
          )}
        </TabPanel>
        <TabPanel value={activeTab} index={3}>
          {activeMeeting && (
            <ErrorBoundary key={`r-${activeMeeting.meeting_id}`} onReset={() => setActiveTab(4)}>
              <RewritePanel meeting={activeMeeting} onSummaryDone={handleRewriteDone} showSnackbar={showSnackbar} />
            </ErrorBoundary>
          )}
        </TabPanel>
        <TabPanel value={activeTab} index={4}>
          <MeetingHistory onSelectMeeting={handleSelectHistory} showSnackbar={showSnackbar} />
        </TabPanel>

        {/* Footer */}
        <Box textAlign="center" mt={6} pb={2}>
          <Typography variant="caption" sx={{ color: '#1E293B', fontSize: 10, fontFamily: '"JetBrains Mono", monospace' }}>
            TANIFY — AI MEETING ASSISTANT · WHISPER + BART + GOOGLE TRANSLATE
          </Typography>
        </Box>
      </Container>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          sx={{ borderRadius: 3, minWidth: 280 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
